import { useEffect, useRef, useState } from "react";
import { useT } from "@/i18n";
import { Toast, useToast } from "@/components/Toast";

const DEVICE_KEY = "myotaku-warehouse:camera-device";
const remembered = () => { try { return localStorage.getItem(DEVICE_KEY) ?? ""; } catch { return ""; } };
const remember = (id: string) => { try { localStorage.setItem(DEVICE_KEY, id); } catch { /* this tab only */ } };

// His PC camera (Z Furniture camOpen / camStart / camShot): a live webcam in high resolution, the
// camera picker remembered on this PC, a flash on each shot, several shots in a row with their
// thumbnails. "media" sends each photo at once (onPhoto); "rec" keeps the pages until "Finish and read
// (n pages)" (onPages). "📱 Use the tablet" and "Choose a file instead" leave the dialog.
export default function CameraDialog({ mode, onPhoto, onPages, onTablet, onFile, onClose, say }: {
  mode: "media" | "rec"; onPhoto?: (file: File) => void; onPages?: (files: File[]) => void;
  onTablet?: () => void; onFile: () => void; onClose: () => void;
  // The opener's own toast, when it has one: his single #toast, whose "Photo added ✓" the opener's
  // "Saved" then replaces — never two toasts on top of each other.
  say?: (message: string) => void;
}) {
  const { t } = useT();
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const [message, setMessage] = useState<string | null>(t("cam.starting"));
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [device, setDevice] = useState(remembered);
  const [current, setCurrent] = useState("");
  const [flash, setFlash] = useState(false);
  const [thumbs, setThumbs] = useState<string[]>([]);
  const [pages, setPages] = useState<File[]>([]);
  // His toast after each shot sent at once: "Photo added ✓" (his #toast, 3.8 s).
  const [toastMsg, toast] = useToast();

  const stop = () => { stream.current?.getTracks().forEach((x) => x.stop()); stream.current = null; };

  useEffect(() => {
    let cancelled = false;
    const want = { width: { ideal: 3840 }, height: { ideal: 2160 } };
    (async () => {
      stop();
      setMessage(t("cam.starting"));
      try {
        let st: MediaStream;
        try {
          st = await navigator.mediaDevices.getUserMedia({ video: device ? { ...want, deviceId: { exact: device } } : { ...want, facingMode: "environment" }, audio: false });
        } catch (e) {
          const n = (e as DOMException).name;
          if (!device || (n !== "OverconstrainedError" && n !== "NotFoundError")) throw e;
          remember("");
          st = await navigator.mediaDevices.getUserMedia({ video: want, audio: false });
        }
        if (cancelled) { st.getTracks().forEach((x) => x.stop()); return; }
        stream.current = st;
        if (video.current) { video.current.srcObject = st; await video.current.play().catch(() => {}); }
        setMessage(null);
        setCurrent(st.getVideoTracks()[0]?.getSettings().deviceId ?? "");
        const all = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput");
        if (!cancelled) setDevices(all);
      } catch (e) {
        // His four messages, by why the camera did not start.
        const err = e as DOMException;
        const n = err?.name;
        setMessage(n === "NotAllowedError" ? (/policy|not allowed in this document/i.test(String(err.message)) ? t("cam.e.blocked") : t("cam.e.denied"))
          : n === "NotFoundError" || n === "OverconstrainedError" ? t("cam.e.none")
          : n === "NotReadableError" || n === "AbortError" ? t("cam.e.busy")
          : n === "SecurityError" ? t("cam.e.blocked") : t("cam.e.other"));
      }
    })();
    return () => { cancelled = true; stop(); };
  }, [device]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => thumbs.forEach((u) => URL.revokeObjectURL(u)), []); // eslint-disable-line react-hooks/exhaustive-deps
  // His Escape closes the camera only: caught first (capture phase) and stopped there, so the dialog the
  // camera was opened from — the finish sheet with its notes — stays open.
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopImmediatePropagation(); onClose(); } };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  }, [onClose]);

  const shot = () => {
    const v = video.current;
    if (!v || !stream.current || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d")?.drawImage(v, 0, 0, c.width, c.height);
    setFlash(true);
    window.setTimeout(() => setFlash(false), 60);
    c.toBlob((b) => {
      if (!b) return;
      const file = new File([b], `photo-${new Date().toISOString().replace(/[:.]/g, "-")}.jpg`, { type: "image/jpeg" });
      setThumbs((l) => [...l, URL.createObjectURL(b)]);
      if (mode === "rec") setPages((l) => [...l, file]);
      else { onPhoto?.(file); (say ?? toast)(t("cam.added")); }
    }, "image/jpeg", 0.92);
  };

  return (
    <div role="dialog" aria-modal="true" aria-label={t("cam.title")} className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-scrim/50 p-4">
      {/* His toast floats over the screen and takes no room, so no button moves under the next tap;
          a tap where it shows goes through to what is underneath. */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-10"><Toast message={toastMsg} /></div>
      <div className="mt-8 w-full max-w-2xl space-y-3 rounded-xl bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-slate-900">{t("cam.title")}</h2>
        <p className="text-sm text-slate-600">{t("cam.hint")}</p>
        <div className="relative overflow-hidden rounded-lg bg-black">
          <video ref={video} autoPlay playsInline muted className="block max-h-[55vh] w-full object-contain" />
          {/* Fixed white on the black picture: the dark theme turns "white" dark, which hid these. */}
          {message && <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm" style={{ color: "#fff" }}>{message}</div>}
          <div className={`pointer-events-none absolute inset-0 transition-opacity ${flash ? "opacity-80" : "opacity-0"}`} style={{ background: "#fff" }} />
        </div>
        {devices.length > 1 && (
          <label className="flex items-center gap-2 text-sm text-slate-700">{t("cam.which")}
            <select value={current} onChange={(e) => { remember(e.target.value); setDevice(e.target.value); }} className="rounded-lg border border-slate-300 px-2 py-1">
              {devices.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{d.label || `${t("cam.which")} ${i + 1}`}</option>)}
            </select>
          </label>
        )}
        {thumbs.length > 0 && <div className="flex flex-wrap gap-2">{thumbs.map((u) => <img key={u} src={u} alt="" className="h-16 w-16 rounded object-cover ring-1 ring-slate-200" />)}</div>}
        <button onClick={shot} disabled={!!message} className="w-full rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40">{t("cam.shot")}</button>
        {onTablet && <button onClick={() => { stop(); onTablet(); }} className="w-full rounded-lg px-4 py-2 text-sm ring-1 ring-slate-300">{t("cam.tab")}</button>}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button onClick={() => { stop(); onFile(); }} className="text-sm text-slate-600 underline">{t("cam.file")}</button>
          <span className="flex gap-2">
            {mode === "rec" && pages.length > 0 && (
              <button onClick={() => { stop(); onPages?.(pages); }} className="rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white">{t(pages.length > 1 ? "cam.doneMany" : "cam.doneOne", { n: pages.length })}</button>
            )}
            <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("cam.close")}</button>
          </span>
        </div>
      </div>
    </div>
  );
}
